/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  CustomerAssignmentCreateResponseOutput,
  CustomerAssignmentPageResponseOutput,
  CustomerAssignmentRemoveResponseOutput,
} from '../model';
import type {
  CustomerAssignmentCreateBody,
  CustomerAssignmentRemoveBody,
  ListCustomerTeamAssignmentsParams,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListCustomerTeamAssignmentsUrl = (
  customerId: string,
  params?: ListCustomerTeamAssignmentsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/customers/${customerId}/team-assignments?${stringifiedParams}`
    : `/api/v1/customers/${customerId}/team-assignments`;
};

export const listCustomerTeamAssignments = async (
  customerId: string,
  params?: ListCustomerTeamAssignmentsParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerAssignmentPageResponseOutput> => {
  return validatedFetch<CustomerAssignmentPageResponseOutput>(
    getListCustomerTeamAssignmentsUrl(customerId, params),
    {
      ...options,
      method: 'GET',

      schema: CustomerAssignmentPageResponseOutput,
    },
  );
};

export const getListCustomerTeamAssignmentsQueryKey = (
  customerId: string,
  params?: ListCustomerTeamAssignmentsParams,
) => {
  return [`/api/v1/customers/${customerId}/team-assignments`, ...(params ? [params] : [])] as const;
};

export const getListCustomerTeamAssignmentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCustomerTeamAssignments>>,
  TError = unknown,
>(
  customerId: string,
  params?: ListCustomerTeamAssignmentsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomerTeamAssignments>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListCustomerTeamAssignmentsQueryKey(customerId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCustomerTeamAssignments>>> = ({
    signal,
  }) => listCustomerTeamAssignments(customerId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: customerId !== null && customerId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCustomerTeamAssignments>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListCustomerTeamAssignmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCustomerTeamAssignments>>
>;
export type ListCustomerTeamAssignmentsQueryError = unknown;

export function useListCustomerTeamAssignments<
  TData = Awaited<ReturnType<typeof listCustomerTeamAssignments>>,
  TError = unknown,
>(
  customerId: string,
  params?: ListCustomerTeamAssignmentsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomerTeamAssignments>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCustomerTeamAssignmentsQueryOptions(customerId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getAddCustomerTeamAssignmentUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}/team-assignments`;
};

export const addCustomerTeamAssignment = async (
  customerId: string,
  customerAssignmentCreateBody: CustomerAssignmentCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerAssignmentCreateResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerAssignmentCreateResponseOutput>(
    getAddCustomerTeamAssignmentUrl(customerId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(customerAssignmentCreateBody),
      schema: CustomerAssignmentCreateResponseOutput,
    },
  );
};

export const getAddCustomerTeamAssignmentMutationKey = () => ['addCustomerTeamAssignment'] as const;

export const getAddCustomerTeamAssignmentMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addCustomerTeamAssignment>>,
    TError,
    AddCustomerTeamAssignmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addCustomerTeamAssignment>>,
  TError,
  AddCustomerTeamAssignmentMutationVariables,
  TContext
> => {
  const mutationKey = getAddCustomerTeamAssignmentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addCustomerTeamAssignment>>,
    AddCustomerTeamAssignmentMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return addCustomerTeamAssignment(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddCustomerTeamAssignmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof addCustomerTeamAssignment>>
>;
export type AddCustomerTeamAssignmentMutationBody = CustomerAssignmentCreateBody;
export type AddCustomerTeamAssignmentMutationError = unknown;
export type AddCustomerTeamAssignmentMutationVariables = {
  customerId: string;
  data: CustomerAssignmentCreateBody;
};

export const useAddCustomerTeamAssignment = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addCustomerTeamAssignment>>,
    TError,
    AddCustomerTeamAssignmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof addCustomerTeamAssignment>>,
  TError,
  AddCustomerTeamAssignmentMutationVariables,
  TContext
> => {
  return useMutation(getAddCustomerTeamAssignmentMutationOptions(options));
};
export const getRemoveCustomerTeamAssignmentUrl = (customerId: string, assignmentId: string) => {
  return `/api/v1/customers/${customerId}/team-assignments/${assignmentId}`;
};

export const removeCustomerTeamAssignment = async (
  customerId: string,
  assignmentId: string,
  customerAssignmentRemoveBody: CustomerAssignmentRemoveBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerAssignmentRemoveResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerAssignmentRemoveResponseOutput>(
    getRemoveCustomerTeamAssignmentUrl(customerId, assignmentId),
    {
      ...options,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(customerAssignmentRemoveBody),
      schema: CustomerAssignmentRemoveResponseOutput,
    },
  );
};

export const getRemoveCustomerTeamAssignmentMutationKey = () =>
  ['removeCustomerTeamAssignment'] as const;

export const getRemoveCustomerTeamAssignmentMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeCustomerTeamAssignment>>,
    TError,
    RemoveCustomerTeamAssignmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeCustomerTeamAssignment>>,
  TError,
  RemoveCustomerTeamAssignmentMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveCustomerTeamAssignmentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeCustomerTeamAssignment>>,
    RemoveCustomerTeamAssignmentMutationVariables
  > = (props) => {
    const { customerId, assignmentId, data } = props ?? {};

    return removeCustomerTeamAssignment(customerId, assignmentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveCustomerTeamAssignmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeCustomerTeamAssignment>>
>;
export type RemoveCustomerTeamAssignmentMutationBody = CustomerAssignmentRemoveBody;
export type RemoveCustomerTeamAssignmentMutationError = unknown;
export type RemoveCustomerTeamAssignmentMutationVariables = {
  customerId: string;
  assignmentId: string;
  data: CustomerAssignmentRemoveBody;
};

export const useRemoveCustomerTeamAssignment = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeCustomerTeamAssignment>>,
    TError,
    RemoveCustomerTeamAssignmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeCustomerTeamAssignment>>,
  TError,
  RemoveCustomerTeamAssignmentMutationVariables,
  TContext
> => {
  return useMutation(getRemoveCustomerTeamAssignmentMutationOptions(options));
};
